import React, { useState } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';

interface Props {
  currentPriority: number;
  onChangePriority: (newPriority: number) => Promise<void>;
  isProcessing: boolean;
}

const PRIORITY_LABELS = {
  0: 'Low',
  1: 'Normal',
  2: 'High',
};

const PRIORITY_COLORS = {
  0: '#9E9E9E',
  1: '#2196F3',
  2: '#F44336',
};

const PrioritySelector: React.FC<Props> = ({ currentPriority, onChangePriority, isProcessing }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [busy, setBusy] = useState(false);

  // Only show when job is processing
  if (!isProcessing) return null;

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget);
  const closeMenu = () => setAnchor(null);

  const handlePriorityChange = async (newPriority: number) => {
    closeMenu();
    if (busy || newPriority === currentPriority) return;

    setBusy(true);
    try {
      await onChangePriority(newPriority);
    } catch (e) {
      console.error('Failed to change priority', e);
    } finally {
      setBusy(false);
    }
  };

  const currentLabel = PRIORITY_LABELS[currentPriority as keyof typeof PRIORITY_LABELS] || 'Normal';
  const currentColor = PRIORITY_COLORS[currentPriority as keyof typeof PRIORITY_COLORS] || '#2196F3';

  return (
    <>
      <PriorityButton
        onClick={openMenu}
        disabled={busy}
        $color={currentColor}
      >
        {busy ? 'Updating...' : `Priority: ${currentLabel}`}
      </PriorityButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => handlePriorityChange(2)}
          disabled={currentPriority === 2}
        >
          <PriorityLabel $color={PRIORITY_COLORS[2]}>
            ● High Priority
          </PriorityLabel>
          {currentPriority === 2 && <CurrentBadge>(Current)</CurrentBadge>}
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange(1)}
          disabled={currentPriority === 1}
        >
          <PriorityLabel $color={PRIORITY_COLORS[1]}>
            ● Normal Priority
          </PriorityLabel>
          {currentPriority === 1 && <CurrentBadge>(Current)</CurrentBadge>}
        </MenuItem>
        <MenuItem
          onClick={() => handlePriorityChange(0)}
          disabled={currentPriority === 0}
        >
          <PriorityLabel $color={PRIORITY_COLORS[0]}>
            ● Low Priority
          </PriorityLabel>
          {currentPriority === 0 && <CurrentBadge>(Current)</CurrentBadge>}
        </MenuItem>
      </Menu>
    </>
  );
};

export default PrioritySelector;

const PriorityButton = styled.button<{ $color: string }>`
  padding: 8px 16px;
  background-color: ${props => props.$color};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const PriorityLabel = styled.span<{ $color: string }>`
  color: ${props => props.$color};
  font-weight: 500;
`;

const CurrentBadge = styled.span`
  margin-left: 8px;
  color: #666;
  font-size: 12px;
`;
